import styled, { css, createGlobalStyle } from 'styled-components';

// --- Paleta de Colores Profesional ---
const colors = {
  primary: '#0052CC',
  background: '#f8f9fa',
  cardBackground: '#FFFFFF',
  textPrimary: '#172B4D',
  textSecondary: '#6B778C',
  border: '#DFE1E6',
  success: '#28a745',
  danger: '#dc3545',
  warning: '#F8981D',
};

// --- Breakpoints (px) ---
const bp = {
  xl: 1400,
  lg: 1200,
  md: 992,
  sm: 768,
  xs: 576,
  xxs: 420,
};

// --- Componentes de Layout ---
export const PageContainer = styled.div`
  background-color: ${colors.background};
  padding: 90px 48px;
  min-height: 100%;

  @media (max-width: ${bp.lg}px) { padding: 72px 36px; }
  @media (max-width: ${bp.sm}px) { padding: 56px 24px; }
  @media (max-width: ${bp.xs}px) { padding: 40px 16px; }
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: ${bp.sm}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

export const Title = styled.h1`
  font-size: clamp(20px, 2.2vw, 24px);
  line-height: 1.2;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0;
  flex-shrink: 0;
  white-space: nowrap;
`;

export const AddUserButton = styled.button`
  background-color: ${colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, box-shadow 0.2s ease;

  &:hover {
    background-color: #0041a3;
    box-shadow: 0 4px 12px rgba(0, 82, 204, 0.2);
  }

  @media (max-width: ${bp.sm}px) {
    width: 100%;
    padding: 12px 16px;
  }
`;

const filterTabButtonStyles = css`
  padding: 8px 16px;
  cursor: pointer;
  border: 1px solid ${colors.primary};
  border-radius: 8px;
  background-color: white;
  color: ${colors.primary};
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease;

  @media (max-width: ${bp.sm}px) {
    flex: 1 1 48%;
  }
  @media (max-width: ${bp.xxs}px) {
    flex: 1 1 100%;
  }

  &.active {
    background-color: ${colors.primary};
    color: white;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

// --- Acciones (filtros de vista + acciones masivas/export) ---
export const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex: 1 1 auto;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: ${bp.sm}px) {
    flex-direction: column;
    align-items: stretch;
    flex: 1 1 100%;
  }
`;

export const FilterButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  button {
    ${filterTabButtonStyles}
  }
`;

export const ActionButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-left: auto;

  @media (max-width: ${bp.sm}px) {
    margin-left: 0;
    width: 100%;
  }
`;

export const ReportAllButton = styled.button`
  background-color: ${colors.warning};
  color: ${colors.textPrimary};
  border: 1px solid #e07f00;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, box-shadow 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #e8870f;
    box-shadow: 0 4px 12px rgba(248, 152, 29, 0.25);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    box-shadow: none;
  }

  @media (max-width: ${bp.sm}px) {
    flex: 1 1 100%;
  }
`;

export const ExportButton = styled.button<{ $active?: boolean }>`
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, color 0.2s ease, border-color 0.2s ease,
    box-shadow 0.2s ease, opacity 0.2s ease;

  ${({ $active }) =>
    $active
      ? css`
          background-color: ${colors.primary};
          color: white;
          border: 1px solid ${colors.primary};

          &:hover:not(:disabled) {
            background-color: #0041a3;
            box-shadow: 0 4px 12px rgba(0, 82, 204, 0.2);
          }
        `
      : css`
          background-color: #f4f5f7;
          color: ${colors.textSecondary};
          border: 1px solid ${colors.border};
          cursor: not-allowed;
          opacity: 0.85;
        `}

  @media (max-width: ${bp.sm}px) {
    flex: 1 1 100%;
  }
`;

// ====== Barra de Componentes/Filtros ======
export const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(180px, 1fr));
  gap: 12px;
  align-items: end;
  background: ${colors.cardBackground};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 16px;
  margin: 12px 0 18px 0;

  /* Escalado progresivo */
  @media (max-width: ${bp.xl}px) { grid-template-columns: repeat(4, minmax(160px, 1fr)); }
  @media (max-width: ${bp.lg}px) { grid-template-columns: repeat(3, minmax(160px, 1fr)); }
  @media (max-width: ${bp.md}px) { grid-template-columns: repeat(2, minmax(160px, 1fr)); }
  @media (max-width: ${bp.sm}px) { grid-template-columns: 1fr; }

  /* Asegura que el botón de Buscar se vea bien en móvil */
  & > button {
    justify-self: start;
    @media (max-width: ${bp.md}px) { justify-self: stretch; }
  }

  .image-field {
    grid-column: span 2;
    @media (max-width: ${bp.lg}px) { grid-column: span 1; }
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0; /* evita overflow de labels largos */
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.textSecondary};
  letter-spacing: .2px;
`;

const baseInput = css`
  height: 40px; /* mejor área táctil */
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 0 12px;
  outline: none;
  font-size: 14px;
  color: ${colors.textPrimary};
  background: #fff;
  width: 100%;
  min-width: 0;

  &:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(0,82,204,0.12);
  }

  @media (max-width: ${bp.xs}px) {
    height: 44px;
    font-size: 15px;
  }
`;

export const StyledSelect = styled.select`
  ${baseInput}
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, ${colors.textSecondary} 50%),
    linear-gradient(135deg, ${colors.textSecondary} 50%, transparent 50%);
  background-position: calc(100% - 18px) 50%, calc(100% - 12px) 50%;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;

  @media (pointer: coarse) {
    /* en móviles deja que el SO use su UI nativa si prefieres */
    /* appearance: auto; */
  }
`;

export const StyledInput = styled.input`${baseInput}`;
export const DateInput   = styled.input`${baseInput}`;

export const ImagePreview = styled.img`
  margin-top: 6px;
  width: 120px;
  height: 26px;
  object-fit: contain;
  border: 1px dashed ${colors.border};
  border-radius: 6px;
  background: #fafbfc;

  @media (max-width: ${bp.xs}px) {
    width: 100%;
    height: 30px;
  }
`;

// --- Contenedor y Estilos del DataGrid ---
export const GridContainer = styled.div`
  background-color: ${colors.cardBackground};
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 4px 8px -2px rgba(9, 30, 66, 0.1), 0 0 1px rgba(9, 30, 66, 0.08);

  /* clave para móviles: permitir scroll horizontal si hace falta */
  overflow-x: auto;

  /* asegura ancho mínimo del grid para que las columnas no colapsen */
  .dx-datagrid {
    min-width: 920px;
  }

  .dx-datagrid-headers {
    border-bottom: 1px solid ${colors.border};
    .dx-header-row {
      color: #000;
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      @media (max-width: ${bp.sm}px) { font-size: 11px; }
    }
  }

  .dx-data-row {
    height: 52px;
    border-bottom: 1px solid ${colors.border} !important;
    color: ${colors.textPrimary};
    &:hover { background-color: #f0f8ff; }
    td { vertical-align: middle; }
  }

  .dx-data-row:last-child { border-bottom: none !important; }

  .dx-command-edit a {
    color: ${colors.textSecondary};
    font-size: 16px;
    margin-right: 8px;
    &:hover { color: ${colors.primary}; }
  }

  .dx-pager {
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap; 
    gap: 10px;

    @media (max-width: ${bp.sm}px) { justify-content: center; }
  }

  .dx-page-info {
    color: ${colors.textSecondary};
    font-size: 14px;
    @media (max-width: ${bp.xs}px) { font-size: 13px; }
  }

  .dx-navigate-button { border: none !important; }
`;

// --- Badge para los Roles ---
interface RoleBadgeProps { role: 'admin' | 'editor' | 'viewer' ; }

export const RoleBadge = styled.span<RoleBadgeProps>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;

  ${({ role }) => {
    let color = colors.success;
    let backgroundColor = '#E3FCEF';
    if (role === 'admin') { color = colors.danger; backgroundColor = '#FFEBE6'; }
    else if (role === 'editor') { color = colors.primary; backgroundColor = '#DEEBFF'; }
    return css` color: ${color}; background-color: ${backgroundColor}; `;
  }}
`;

// --- Estilos Globales para Modales de DevExtreme ---
export const DevExtremeModalGlobalStyles = createGlobalStyle`
  .dx-popup-wrapper .dx-popup-content {
    border-radius: 12px !important;
    box-shadow: 0 8px 16px -4px rgba(9, 30, 66, 0.25),
      0 0 2px rgba(9, 30, 66, 0.08) !important;
    padding: 0 !important;
  }

  .dx-overlay-content .dx-popup-title {
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0,0,0,0.1);
    background-color: ${colors.warning} !important; 

    .dx-toolbar-label {
      font-size: 18px !important;
      font-weight: 600 !important;
      color: white !important;
    }
    .dx-closebutton .dx-icon { color: white !important; }
  }

  .dx-popup-content .dx-form { padding: 24px; background-color: #FFFFFF; }
  .dx-form-item > .dx-field-item-label { font-weight: 600 !important; }

  .dx-popup-bottom {
    padding: 16px 24px;
    border-top: 1px solid ${colors.border};
    background-color: #FFFFFF !important;
  }

  .custom-dialog-message {
    font-size: 16px;
    color: ${colors.textPrimary};
    padding: 24px;
    text-align: center;
  }

  .dx-dialog-buttons .dx-button {
    font-weight: 500 !important;
    padding: 8px 20px !important;
    transition: all 0.2s ease-in-out;
  }

  .dx-dialog-buttons .dx-button.dx-button-success {
    background-color: transparent !important;
    border: 1px solid ${colors.success} !important;
    color: ${colors.success} !important;
    padding: 0px 20px !important;
    &:hover { background-color: #e9f7eb !important; color: #1a6e2c !important; }
  }

  .dx-dialog-buttons .dx-button.dx-button-danger {
    background-color: transparent !important;
    border: 1px solid ${colors.danger} !important;
    padding: 0px 20px !important;
    color: ${colors.danger} !important;
    &:hover { background-color: #fcebeb !important; color: #a52121 !important; }
  }

  /* Menos animaciones para usuarios con reduce-motion */
  @media (prefers-reduced-motion: reduce) {
    * { transition: none !important; animation: none !important; }
  }
`;
